import React, { ReactNode, cloneElement, isValidElement } from 'react'

import escapeRegExp from 'lodash/escapeRegExp'

export { capitalize } from '@utils/capitalize'

export const isString = (value: ReactNode): value is string =>
  typeof value === 'string'

interface TruncateOptions {
  length: number
  omission: string
}
const defaultTruncateOptions: TruncateOptions = {
  length: 30,
  omission: '…',
}
export function truncate(
  value: string,
  options: Partial<TruncateOptions> = defaultTruncateOptions
) {
  const { length, omission } = { ...defaultTruncateOptions, ...options }

  if (value.length > length) {
    return value.substring(0, length) + omission
  }
  return value
}

function stripToNumbers(value: string) {
  return value.replace(/[^\d]/g, '')
}

export function formatAsPhoneNumber(phoneNumber: string) {
  const cleaned = phoneNumber.replace(/\D/g, '')
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/)

  if (match) {
    const intlCode = match[1] ? '+1 ' : ''
    return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('')
  }

  return phoneNumber
}

export function getFileExtension(filename: string) {
  const isBase64 = filename.includes(';base64,')
  if (isBase64) return filename.split('/')[1]?.split(';')[0]
  return filename.split('.').pop()?.toLowerCase()
}

export function isSupportedImageExtension(extension?: string) {
  return /jpeg|jpg|png|gif/.test(extension?.toLowerCase() ?? '')
}

export function toPossessive(name: string) {
  return `${name}'${name.endsWith('s') ? '' : 's'}`
}

type ExtractLinksFromStringOptions = {
  allowedDomains?: string[]
  shouldAllowLink?: (link: string, isInAllowedDomains: boolean) => boolean
  renderAsNodes?: (link: string) => React.ReactNode
}

const urlRegEx =
  /(http(s)?:\/{2})?(?!www | www\.)[A-Za-z0-9_-]+\.+[A-Za-z0-9./%&=?_:;-]+/gi

export function extractLinks(
  value: string,
  {
    shouldAllowLink,
    allowedDomains,
    renderAsNodes,
  }: ExtractLinksFromStringOptions
): {
  links: string[]
  nodes: React.ReactNode[]
} {
  // using this timestamp to create a more precise placemarker
  const timestamp = new Date().valueOf()
  const linkPlacemarker = `%link_at_${timestamp}%`

  // get all of the links in the string
  const links = value.match(urlRegEx) ?? ([] as string[])

  // create a version of the message with links place marked
  const messageWithLinkPlacemarkers = value.replace(urlRegEx, (url) => {
    const indexOfLink = links.indexOf(url)

    if (indexOfLink === -1) return url

    const allowedDomainsPiped =
      allowedDomains?.map((domain) => `(${escapeRegExp(domain)})`).join('|') ??
      ''

    const allowedDomainsRegEx = new RegExp(allowedDomainsPiped, 'gi')

    const isInAllowedDomains = allowedDomainsRegEx.test(url)

    if (typeof shouldAllowLink === 'function') {
      if (!shouldAllowLink(url, isInAllowedDomains)) return url
    } else if (!isInAllowedDomains) return url

    return `${linkPlacemarker}[link ${indexOfLink}]${linkPlacemarker}`
  })

  // break the message into an array of parts like ["part of a message", "https://somelink.com", "the rest of the message"]
  const messageParts = messageWithLinkPlacemarkers.split(linkPlacemarker)

  if (!renderAsNodes) return { links, nodes: [] }

  // generate nodes for messages and links
  const nodes = messageParts.map((part) => {
    const [, matchingLinkIndex] = part.match(/\[link (\d+)\]/) ?? []

    if (!matchingLinkIndex) return part

    const link = links[+matchingLinkIndex]

    return renderAsNodes(link)
  })

  return { links, nodes }
}

export function slugify(str: string): string {
  return str
    .normalize('NFKD')
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

const TEMPLATE_RE = /{{\s*(\w+)\s*}}/g
type ReplacementDict = Record<string, string | number>
type ComplexReplacementDict = Record<string, string | number | ReactNode>
type ValueObject = string | { one: string; other: string }

type Trim<S extends string> = S extends ` ${infer Rest}`
  ? Trim<Rest>
  : S extends `${infer Rest} `
    ? Trim<Rest>
    : S

type Placeholders<S extends string> =
  S extends `${string}{{${infer Key}}}${infer Rest}`
    ? Trim<Key> | Placeholders<Rest>
    : never

type PlaceholderKeys<V extends ValueObject> = V extends string
  ? Placeholders<V>
  : V extends { one: string; other: string }
    ? Placeholders<V['one']> | Placeholders<V['other']>
    : never

type Replacements<V extends ValueObject, T> = [PlaceholderKeys<V>] extends [
  never,
]
  ? Record<string, T>
  : Record<PlaceholderKeys<V>, T> & { count?: number }

/** Stable log line so Datadog can alert on an exact query, not per-placeholder text. */
export const TEMPLATE_UNRESOLVED_LOG_MESSAGE =
  'template(): no replacement provided for a copy placeholder'

const reportUnresolvedPlaceholder = (placeholder: string, copy: string) => {
  const error = new Error(
    `template(): no replacement provided for "{{${placeholder}}}"`
  )

  if (process.env.NODE_ENV !== 'production') {
    throw new Error(
      `${error.message}\n\nIn: "${copy}"\n\nIf yarn typecheck did not flag this, the copy string's type widened to \`string\`, so the placeholder names were invisible to the compiler and it checked nothing. Declare the strings \`as const\` where they are defined.`
    )
  }

  if (typeof window === 'undefined') {
    console.error(TEMPLATE_UNRESOLVED_LOG_MESSAGE, { placeholder, copy })
    return
  }

  void import('@services/datadog/datadog-logs')
    .then(({ datadogLogs }) => {
      datadogLogs.logger.error(
        TEMPLATE_UNRESOLVED_LOG_MESSAGE,
        { placeholder, copy },
        error
      )
    })
    .catch(() => {
      console.error(TEMPLATE_UNRESOLVED_LOG_MESSAGE, { placeholder, copy })
    })
}

function getValueString(
  valueObject: ValueObject,
  replacements: ReplacementDict | ComplexReplacementDict
) {
  if (typeof valueObject === 'string') {
    return valueObject
  }

  const hasCount = 'count' in replacements
  const hasOne = 'one' in valueObject

  if (hasCount && hasOne && replacements.count === 1) {
    return valueObject.one
  }

  return valueObject.other
}

export function template<V extends ValueObject>(
  value: V,
  replacements: Replacements<V, string | number>
): string
export function template<V extends ValueObject>(
  value: V,
  replacements: Replacements<V, string | number | ReactNode>
): ReactNode[]
export function template(
  value: ValueObject,
  replacements: ReplacementDict | ComplexReplacementDict
): string | ReactNode[] {
  const parts: (string | ReactNode)[] = []
  let matchIndex = 0
  let lastOffset = 0

  const valueStr = getValueString(value, replacements)

  try {
    valueStr.replace(
      TEMPLATE_RE,
      (match, replacementKey: string, offset: number) => {
        matchIndex += 1

        // push the source text before the first match if it exists
        const previousString = valueStr.substring(lastOffset, offset)
        if (previousString) parts.push(previousString)

        lastOffset = offset + match.length

        const replacementValue = replacements[replacementKey]

        // Nullish is the trigger so an explicit undefined is caught too; empty
        // string stays valid (summer-pass early-bird copy relies on it).
        if (replacementValue == null) {
          reportUnresolvedPlaceholder(replacementKey, valueStr)
          parts.push(match)
          return ''
        }

        if (isValidElement(replacementValue)) {
          parts.push(cloneElement(replacementValue, { key: matchIndex }))
        } else {
          parts.push(String(replacementValue))
        }

        return ''
      }
    )
  } finally {
    TEMPLATE_RE.lastIndex = 0
  }

  // push the last part of the source text
  const lastPart = valueStr.substring(lastOffset)
  if (lastPart) parts.push(lastPart)

  return parts.every(isString) ? parts.join('') : parts
}

export default {
  stripToNumbers,
  formatAsPhoneNumber,
  getFileExtension,
  isSupportedImageExtension,
  toPossessive,
  extractLinks,
}
